import React, { useState } from 'react';
import { Input, Checkbox, Button, Select } from '@fluentui/react-components';
import { Checkmark16Regular, Dismiss16Regular } from '@fluentui/react-icons';
import { Transaction, ACTION_OPTIONS } from '../types';
import '../styles/register.css';

interface EditableRowProps {
  txn: Transaction;
  balance: number;
  onSave: (id: number, data: { date: string; action: string; payee: string; payment: number | null; deposit: number | null; notes: string; is_eom?: boolean }) => void;
  onCancel: () => void;
  onToggleCleared: (id: number) => void;
}

function formatMoney(amount: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
}

export const EditableRow: React.FC<EditableRowProps> = ({ txn, balance, onSave, onCancel, onToggleCleared }) => {
  const [date, setDate] = useState(txn.date);
  const [action, setAction] = useState(txn.action as string);
  const [payee, setPayee] = useState(txn.payee);
  const [payment, setPayment] = useState(txn.payment !== null ? String(txn.payment) : '');
  const [deposit, setDeposit] = useState(txn.deposit !== null ? String(txn.deposit) : '');
  const [notes, setNotes] = useState(txn.notes || '');
  const [isEom, setIsEom] = useState(txn.is_eom || false);

  const handleSave = () => {
    if (!payee.trim()) return;
    onSave(txn.id, {
      date,
      action,
      payee: payee.trim(),
      payment: payment ? parseFloat(payment) : null,
      deposit: deposit ? parseFloat(deposit) : null,
      notes: notes.trim(),
      is_eom: action === 'Memo' && isEom,
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSave();
    if (e.key === 'Escape') onCancel();
  };

  return (
    <tr className="register-row editing" onKeyDown={handleKeyDown}>
      <td className="col-date">
        <Input type="date" value={date} onChange={(e, d) => setDate(d.value)} size="small" />
      </td>
      <td className="col-action">
        <Select value={action} onChange={(e, d) => setAction(d.value)} size="small">
          {ACTION_OPTIONS.map(a => <option key={a} value={a}>{a}</option>)}
        </Select>
      </td>
      <td className="col-payee">
        <Input value={payee} onChange={(e, d) => setPayee(d.value)} size="small" autoFocus />
      </td>
      <td className="col-money">
        <Input type="number" value={payment} onChange={(e, d) => setPayment(d.value)}
          size="small" style={{ textAlign: 'right' }} />
      </td>
      <td className="col-money">
        <Input type="number" value={deposit} onChange={(e, d) => setDeposit(d.value)}
          size="small" style={{ textAlign: 'right' }} />
      </td>
      <td className={`col-money col-balance ${balance < 0 ? 'negative' : ''}`}>
        {action !== 'Memo' ? formatMoney(balance) : ''}
      </td>
      <td className="col-notes">
        <Input value={notes} onChange={(e, d) => setNotes(d.value)} size="small" />
      </td>
      <td className="col-clr">
        {action === 'Memo' ? (
          <Checkbox
            checked={isEom}
            onChange={(e, d) => setIsEom(!!d.checked)}
            title="End of Month marker"
          />
        ) : (
          <Checkbox checked={txn.cleared} onChange={() => onToggleCleared(txn.id)} />
        )}
      </td>
      <td className="col-actions">
        <Button appearance="subtle" size="small" icon={<Checkmark16Regular />}
          onClick={handleSave} title="Save" style={{ color: '#27ae60' }} />
        <Button appearance="subtle" size="small" icon={<Dismiss16Regular />}
          onClick={onCancel} title="Cancel" style={{ color: '#e74c3c' }} />
      </td>
    </tr>
  );
};
